import { quizSetSchema, type ClassicQuiz, type MediaAssetDescriptor, type QuizQuestion } from "@quizshow/protocol";

interface LegacyClassicQuestion {
  question: string;
  answers: string[];
  answerCount?: number;
  correct: number | string | Array<number | string>;
  fiftyRemoveCount?: number;
  isBonus?: boolean;
  backgroundImage?: unknown;
  questionImage?: unknown;
  questionImageLarge?: unknown;
  questionSound?: unknown;
  questionSoundName?: unknown;
}

interface LegacyRankStyle {
  bg?: unknown;
  text?: unknown;
  logo?: unknown;
}

interface LegacyClassicSettings {
  jokerCounts?: unknown;
  jokerPreset?: unknown;
  soundVolume?: unknown;
  ranks?: unknown;
  rankStyles?: unknown;
  theme?: unknown;
}

interface LegacyClassicObject {
  settings?: LegacyClassicSettings;
  questions: LegacyClassicQuestion[];
}

export interface PreparedLegacyMedia {
  descriptor: MediaAssetDescriptor;
  blob: Blob;
}

export interface LegacyClassicConversion {
  quiz: ClassicQuiz;
  media: PreparedLegacyMedia[];
}

const imageMimeTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
const audioMimeTypes = new Set(["audio/mpeg", "audio/ogg", "audio/wav", "audio/x-wav"]);
const defaultTheme = {
  primaryColor: "#8f1838",
  accentColor: "#ef4565",
  backgroundColor: "#100b11",
};

function quizTitleFromFileName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^.]+$/, "");
  return withoutExtension.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim() || "Importiertes Classic Quiz";
}

function isLegacyClassicQuestion(value: unknown): value is LegacyClassicQuestion {
  if (!value || typeof value !== "object") return false;
  const question = value as Partial<LegacyClassicQuestion>;
  return typeof question.question === "string" && Array.isArray(question.answers) && "correct" in question;
}

export function isLegacyClassicArray(value: unknown): value is LegacyClassicQuestion[] {
  return Array.isArray(value) && value.length > 0 && value.every(isLegacyClassicQuestion);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function isLegacyClassicObject(value: unknown): value is LegacyClassicObject {
  if (!isRecord(value) || !Array.isArray(value.questions) || value.questions.length === 0) return false;
  return value.questions.every(isLegacyClassicQuestion) && (!("settings" in value) || isRecord(value.settings));
}

export function isLegacyClassic(value: unknown): value is LegacyClassicQuestion[] | LegacyClassicObject {
  return isLegacyClassicArray(value) || isLegacyClassicObject(value);
}

function bytesFromBase64(base64: string): ArrayBuffer {
  const binary = atob(base64.replace(/\s+/g, ""));
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return buffer;
}

function embeddedImage(value: unknown): { blob: Blob; extension: string } | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const source = value.trim();
  const dataUrl = /^data:(image\/(?:jpeg|png|webp));base64,(.+)$/is.exec(source);
  if (dataUrl) {
    const mimeType = dataUrl[1]!.toLowerCase();
    if (!imageMimeTypes.has(mimeType)) return undefined;
    const bytes = bytesFromBase64(dataUrl[2]!);
    return {
      blob: new Blob([bytes], { type: mimeType }),
      extension: mimeType === "image/jpeg" ? "jpg" : mimeType.split("/")[1]!,
    };
  }

  const compact = source.replace(/\s+/g, "");
  const detected =
    compact.startsWith("iVBOR") ? { mimeType: "image/png", extension: "png" }
    : compact.startsWith("/9j/") ? { mimeType: "image/jpeg", extension: "jpg" }
    : compact.startsWith("UklGR") ? { mimeType: "image/webp", extension: "webp" }
    : undefined;
  if (!detected) return undefined;
  return {
    blob: new Blob([bytesFromBase64(compact)], { type: detected.mimeType }),
    extension: detected.extension,
  };
}

function embeddedAudio(value: unknown): { blob: Blob; extension: string } | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const source = value.trim();
  const dataUrl = /^data:(audio\/(?:mpeg|ogg|wav|x-wav));base64,(.+)$/is.exec(source);
  if (dataUrl) {
    const mimeType = dataUrl[1]!.toLowerCase();
    if (!audioMimeTypes.has(mimeType)) return undefined;
    return {
      blob: new Blob([bytesFromBase64(dataUrl[2]!)], { type: mimeType }),
      extension: mimeType === "audio/mpeg" ? "mp3" : mimeType === "audio/ogg" ? "ogg" : "wav",
    };
  }

  const compact = source.replace(/\s+/g, "");
  const detected =
    compact.startsWith("SUQz") || compact.startsWith("/+M")
      ? { mimeType: "audio/mpeg", extension: "mp3" }
      : compact.startsWith("T2dnUw")
        ? { mimeType: "audio/ogg", extension: "ogg" }
        : compact.startsWith("UklGR")
          ? { mimeType: "audio/wav", extension: "wav" }
          : undefined;
  if (!detected) return undefined;
  return {
    blob: new Blob([bytesFromBase64(compact)], { type: detected.mimeType }),
    extension: detected.extension,
  };
}

function correctIndexes(value: LegacyClassicQuestion["correct"], answerCount: number): number[] {
  const entries = Array.isArray(value) ? value : [value];
  const indexes = entries.map((entry) => typeof entry === "number" ? entry : Number.parseInt(entry, 10));
  if (indexes.some((index) => !Number.isInteger(index) || index < 0 || index >= answerCount)) {
    throw new Error(`Der richtige Antwortindex muss zwischen 0 und ${answerCount - 1} liegen.`);
  }
  return [...new Set(indexes)];
}

function imageReference(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function normalizedColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const color = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(color)) return color;
  const short = /^#([0-9a-fA-F]{3})$/.exec(color);
  return short
    ? `#${short[1]!.split("").map((character) => character.repeat(2)).join("")}`
    : fallback;
}

function legacyTheme(settings: LegacyClassicSettings | undefined) {
  const theme = isRecord(settings?.theme) ? settings.theme : {};
  const colors = isRecord(theme.colors) ? theme.colors : {};
  return {
    name: typeof theme.name === "string" && theme.name.trim() ? theme.name.trim() : undefined,
    primaryColor: normalizedColor(colors.accent ?? colors.panel, defaultTheme.primaryColor),
    accentColor: normalizedColor(colors.selected ?? colors.good ?? colors.accent, defaultTheme.accentColor),
    backgroundColor: normalizedColor(colors.bg, defaultTheme.backgroundColor),
    textColor: normalizedColor(colors.text, "#fbeff3"),
  };
}

function rankStyleAt(settings: LegacyClassicSettings | undefined, index: number): LegacyRankStyle {
  if (!Array.isArray(settings?.rankStyles) || !isRecord(settings.rankStyles[index])) return {};
  return settings.rankStyles[index] as LegacyRankStyle;
}

function looksLikeMediaReference(value: string): boolean {
  return /^(?:data:|https?:|blob:|[./\\])|[.](?:png|jpe?g|webp|gif|svg)$/i.test(value);
}

function normalizedKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function recordEntry(record: unknown, aliases: string[]): { found: boolean; value?: unknown } {
  if (!isRecord(record)) return { found: false };
  const aliasSet = new Set(aliases.map(normalizedKey));
  const entry = Object.entries(record).find(([key]) => aliasSet.has(normalizedKey(key)));
  return entry ? { found: true, value: entry[1] } : { found: false };
}

function numericCount(value: unknown, fallback: number): number {
  if (typeof value === "boolean") return value ? fallback : 0;
  const direct = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  if (Number.isFinite(direct)) return Math.min(100, Math.max(0, Math.trunc(direct)));
  if (isRecord(value)) {
    for (const key of ["startingCount", "count", "amount", "start"]) {
      if (key in value) return numericCount(value[key], fallback);
    }
    if (value.enabled === false) return 0;
  }
  return fallback;
}

function textProperty(value: unknown, keys: string[]): string | undefined {
  if (!isRecord(value)) return undefined;
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return undefined;
}

function legacyJokers(settings: LegacyClassicSettings | undefined, accentColor: string) {
  const definitions = [
    {
      aliases: ["fifty", "5050", "fiftyfifty"],
      name: "50:50",
      type: "fifty-fifty" as const,
      icon: "50",
    },
    {
      aliases: ["phone", "telefon", "telephone"],
      name: "Telefonjoker",
      type: "custom" as const,
      icon: "Tel",
    },
    {
      aliases: ["mod", "moderator", "host"],
      name: "Moderatorjoker",
      type: "custom" as const,
      icon: "Mod",
    },
  ];
  return definitions.flatMap((definition) => {
    const countEntry = recordEntry(settings?.jokerCounts, definition.aliases);
    const presetEntry = recordEntry(settings?.jokerPreset, definition.aliases);
    if (!countEntry.found && !presetEntry.found) return [];
    if (presetEntry.value === false && !countEntry.found) return [];
    const startingCount = numericCount(
      countEntry.found ? countEntry.value : presetEntry.value,
      1,
    );
    return [{
      jokerId: crypto.randomUUID(),
      name: textProperty(presetEntry.value, ["name", "label", "title"]) ?? definition.name,
      description: textProperty(presetEntry.value, ["description", "text"]),
      color: accentColor,
      icon: definition.icon,
      type: definition.type,
      startingCount,
    }];
  });
}

function preparedMedia(
  media: PreparedLegacyMedia[],
  source: { blob: Blob; extension: string } | undefined,
  kind: MediaAssetDescriptor["kind"],
  fileName: string,
  maxBytes: number,
): string | undefined {
  if (!source) return undefined;
  if (source.blob.size > maxBytes) {
    throw new Error(`Das Legacy-Medium "${fileName}" überschreitet ${Math.round(maxBytes / 1024 / 1024)} MB.`);
  }
  const mediaId = crypto.randomUUID();
  media.push({
    descriptor: {
      mediaId,
      kind,
      fileName,
      mimeType: source.blob.type,
      bytes: source.blob.size,
    },
    blob: source.blob,
  });
  return mediaId;
}

export function convertLegacyClassic(value: unknown, fileName: string): LegacyClassicConversion {
  if (!isLegacyClassic(value)) {
    throw new Error("Die Datei entspricht nicht dem unterstützten Legacy-Classic-Format.");
  }

  const settings = isLegacyClassicObject(value) ? value.settings : undefined;
  const legacyQuestions = isLegacyClassicObject(value) ? value.questions : value;
  const theme = legacyTheme(settings);
  const media: PreparedLegacyMedia[] = [];
  const quizReferences: string[] = [];
  const ranks = (Array.isArray(settings?.ranks) ? settings.ranks : [])
    .map((rank) => String(rank).trim())
    .filter(Boolean)
    .map((name, rankIndex) => {
      const style = rankStyleAt(settings, rankIndex);
      const logoReference = imageReference(style.logo);
      const logoImage = embeddedImage(style.logo);
      const imageMediaId = preparedMedia(
        media,
        logoImage,
        "icon",
        `legacy-rang-${rankIndex + 1}.${logoImage?.extension ?? "png"}`,
        5 * 1024 * 1024,
      );
      if (logoReference && !logoImage && looksLikeMediaReference(logoReference)) {
        quizReferences.push(`Rang "${name}": ${logoReference}`);
      }
      return {
        rankId: crypto.randomUUID(),
        name,
        color: normalizedColor(style.text, theme.textColor),
        backgroundColor: normalizedColor(style.bg, theme.primaryColor),
        icon:
          logoReference && !logoImage && !looksLikeMediaReference(logoReference) && logoReference.length <= 16
            ? logoReference
            : undefined,
        imageMediaId,
        requiredProgress: rankIndex,
      };
    });
  const jokers = legacyJokers(settings, theme.accentColor);
  const questions: QuizQuestion[] = legacyQuestions.map((legacy, questionIndex) => {
    const configuredCount =
      Number.isInteger(legacy.answerCount) && Number(legacy.answerCount) > 0
        ? Math.min(Number(legacy.answerCount), legacy.answers.length)
        : legacy.answers.length;
    const indexedAnswers = legacy.answers
      .slice(0, configuredCount)
      .map((answer, legacyIndex) => ({ text: String(answer).trim(), legacyIndex }))
      .filter((answer) => Boolean(answer.text));
    if (indexedAnswers.length < 2) {
      throw new Error(`Legacy-Frage ${questionIndex + 1} benötigt mindestens zwei Antworten.`);
    }
    const correct = correctIndexes(legacy.correct, configuredCount);
    if (correct.some((index) => !indexedAnswers.some((answer) => answer.legacyIndex === index))) {
      throw new Error(`Die richtige Antwort in Legacy-Frage ${questionIndex + 1} ist leer.`);
    }
    const incorrectCount = indexedAnswers.length - correct.length;
    if (incorrectCount < 1) {
      throw new Error(`Legacy-Frage ${questionIndex + 1} benötigt mindestens eine falsche Antwort.`);
    }

    const largeSource =
      typeof legacy.questionImageLarge === "string"
        ? embeddedImage(legacy.questionImageLarge)
        : undefined;
    const normalSource = embeddedImage(legacy.questionImage);
    const preparedImage = largeSource ?? normalSource;
    const backgroundSource = embeddedImage(legacy.backgroundImage);
    const audioSource = embeddedAudio(legacy.questionSound);
    const imageMediaId = preparedMedia(
      media,
      preparedImage,
      "image",
      `legacy-frage-${questionIndex + 1}.${preparedImage?.extension ?? "png"}`,
      5 * 1024 * 1024,
    );
    const backgroundMediaId = preparedMedia(
      media,
      backgroundSource,
      "background",
      `legacy-frage-${questionIndex + 1}-hintergrund.${backgroundSource?.extension ?? "png"}`,
      5 * 1024 * 1024,
    );
    const requestedAudioName = imageReference(legacy.questionSoundName);
    const audioMediaId = preparedMedia(
      media,
      audioSource,
      "audio",
      requestedAudioName && /[.][a-z0-9]{2,5}$/i.test(requestedAudioName)
        ? requestedAudioName
        : `legacy-frage-${questionIndex + 1}.${audioSource?.extension ?? "mp3"}`,
      15 * 1024 * 1024,
    );
    const unhandledReferences = [
      ["Hintergrund", legacy.backgroundImage, backgroundSource],
      ["Fragenbild", legacy.questionImage, normalSource],
      [
        "Großes Fragenbild",
        typeof legacy.questionImageLarge === "string" ? legacy.questionImageLarge : undefined,
        largeSource,
      ],
      ["Audio", legacy.questionSound, audioSource],
    ]
      .flatMap(([label, reference, embedded]) => {
        const text = imageReference(reference);
        return text && !embedded ? [`${label}: ${text}`] : [];
      });
    const isBonus = Boolean(legacy.isBonus);
    return {
      questionId: crypto.randomUUID(),
      prompt: legacy.question.trim() || `Legacy-Frage ${questionIndex + 1}`,
      answers: indexedAnswers.map(({ text, legacyIndex }) => ({
        optionId: crypto.randomUUID(),
        text,
        isCorrect: correct.includes(legacyIndex),
      })),
      adminNotes: unhandledReferences.length
        ? `Legacy-Medienreferenzen (nicht eingebettet): ${unhandledReferences.join(" | ")}`
        : undefined,
      timerEnabled: true,
      timerSeconds: 300,
      points: 100,
      media: {
        imageMediaId,
        backgroundMediaId,
        audioMediaId,
        imageSize: largeSource || legacy.questionImageLarge === true ? "large" : "normal",
      },
      fiftyFiftyRemoveCount: Math.min(
        Math.max(1, Number(legacy.fiftyRemoveCount) || 1),
        incorrectCount,
      ),
      questionType: isBonus ? "bonus" : "normal",
      bonusReward: isBonus ? { type: "points", amount: 100 } : undefined,
      answerMode: "multiple-choice",
      selectionMode: correct.length > 1 ? "multiple" : "single",
      evaluationMode: "manual",
    };
  });

  if (media.reduce((total, entry) => total + entry.blob.size, 0) > 100 * 1024 * 1024) {
    throw new Error("Der Legacy-Import überschreitet das Medienlimit von 100 MB.");
  }
  const titleFromFile = quizTitleFromFileName(fileName);
  const useThemeNameAsTitle = theme.name && theme.name.toLowerCase() !== "quizshow studio";
  const descriptionParts = ["Aus einem Legacy-Classic-Quiz importiert."];
  if (theme.name) descriptionParts.push(`Legacy-Thema: ${theme.name}.`);
  if (typeof settings?.soundVolume === "number") {
    descriptionParts.push(`Legacy-Lautstärke: ${settings.soundVolume}%.`);
  }
  if (quizReferences.length) {
    descriptionParts.push(`Nicht eingebettete Rangmedien: ${quizReferences.join(" | ")}.`);
  }
  const quiz = quizSetSchema.parse({
    formatVersion: 1,
    quizId: crypto.randomUUID(),
    moduleId: "classic",
    moduleVersion: 1,
    title: useThemeNameAsTitle ? theme.name : titleFromFile,
    description: descriptionParts.join(" "),
    updatedAt: new Date().toISOString(),
    theme: {
      primaryColor: theme.primaryColor,
      accentColor: theme.accentColor,
      backgroundColor: theme.backgroundColor,
    },
    mediaAssets: media.map((entry) => entry.descriptor),
    allowSelfTeamSelection: false,
    allowParticipantJokers: jokers.length > 0,
    autoOpenBuzzer: false,
    scoringMode: ranks.length ? "rank-progress" : "points",
    ranks,
    jokers,
    usesLives: false,
    defaultLives: 3,
    questions,
  });
  if (quiz.moduleId !== "classic") {
    throw new Error("Der Legacy-Import konnte nicht als Classic Quiz validiert werden.");
  }
  return { quiz, media };
}
